import useDidMount from "@/hooks/useDidMount"
import { handleBackButton } from "@/utils/backButton"
import { isSmallScreen } from "@/utils/checkDevice"
import React, { useState } from "react"
import ActionDialog from "@/components/ActionDialog"
import { renderComponent } from "@/utils/node"
import InstanceListItem, { InstanceListItemView } from "@/components/InstanceSelectorDialogue/InstanceListItem"
import Divider from "@/components/Divider"
import Instance from "@/services/Instance"
import { Grid, Typography, Divider as MUIDivider, Link, TextField, InputAdornment, IconButton } from "@material-ui/core"
import Loading from "@/components/Loading"
import { UserInstance } from "@/services/User"
import useStyles from "@/components/InstanceSelectorDialogue/styles"
import {
	PeopleOutlineTwoTone as PeopleOutlineTwoToneIcon,
	Warning as WarningIcon,
	Search as SearchIcon,
	Close as CloseIcon
} from "@material-ui/icons"
import { moveIndexToFirstPosition } from "@/utils/array"
import AlertContainer from "@/components/AlertContainer"
import { colors } from "@/styles/colors"
import HardCoded from "@/services/HardCoded"
import useValidation from "@/hooks/useValidation"
import { regexList } from "@/utils/regex"

type InstanceSelectorDialogueProps = {
	allUserInstances: UserInstance[]
	actualUserInstanceId: number
	isAdminView: boolean
}

type EnrichedUserInstances = UserInstance & {
	viewType: InstanceListItemView
}

type InstanceSelectorDialogueType = {
	open: (props: InstanceSelectorDialogueProps) => void
}

type InstanceSelectorSearchQuery = {
	name: string | null
	instanceId: number | null
	inputText: string
}

const InstanceSelectorDialogue: InstanceSelectorDialogueType & React.FC<InstanceSelectorDialogueProps> = (props) => {
	const {
		allUserInstances,
		actualUserInstanceId,
		isAdminView
	} = props

	const classes = useStyles()
	const [opened, setOpened] = useState(true)
	const [loading, setLoading] = useState(false)
	const [searchQuery, setSearchQuery] = useState<InstanceSelectorSearchQuery>({ name: null, instanceId: null, inputText: "" })

	const { validation } = useValidation()
	const canViewUninassauFeaturePage = HardCoded.checkFeatureFlag("canViewUninassauFeaturePage")

	const handleClose = async () => {
		if (!loading) {
			setOpened(false)
		}
	}

	const handleSearchChange = (searchText: string) => {
		if (!searchText || searchText.trim().length === 0) {
			setSearchQuery({ name: null, instanceId: null, inputText: "" })
			return
		}

		const hasOnlyNumber = regexList.has_only_number.test(searchText.trim())

		if (hasOnlyNumber) {
			setSearchQuery({ name: null, instanceId: Number(searchText.trim()), inputText: searchText })
			return
		}

		setSearchQuery({ name: searchText.trim(), instanceId: null, inputText: searchText })
	}

	/**
			* Filtering is intentionally done on the front-end instead of delegating to the backend.
			* In this case, the list of instances is globally loaded during route initialization,
			* not through a local service as in other parts of the system.
			*
			* Fetching filtered data from the backend here could introduce unnecessary complexity
			* and potential side effects across the application, since this global data is shared
			* and critical to other flows.
			*
			* To avoid those risks, we opted to perform client-side filtering only at the point of display.
			* This ensures performance and stability without compromising data integrity.
	  */
	const getEnrichedAndOrderedUserInstances = (
		userInstances: UserInstance[],
		searchQuery: InstanceSelectorSearchQuery
	): EnrichedUserInstances[] => {
		let filtered = userInstances

		if (searchQuery.inputText && searchQuery.inputText.trim().length > 0) {
			filtered = userInstances.filter((instance) => {
				const searchText = searchQuery.inputText.trim().toLowerCase()

				const nameMatches = instance.nickname.toLowerCase().includes(searchText)

				const instanceIdMatches = instance.instance_id.toString().includes(searchText)

				return nameMatches || instanceIdMatches
			})
		}

		let indexOfInstanceWhoseUserIsCurrentlyLoggedIn: number | null = null
		const enrichedUserInstances: EnrichedUserInstances[] = []

		filtered.forEach((userInstance, userInstanceIndex) => {
			let viewType: InstanceListItemView
			if (isAdminView) {
				viewType = "admin"
			} else if (userInstance.instance_id === actualUserInstanceId) {
				viewType = "already_logged"
				indexOfInstanceWhoseUserIsCurrentlyLoggedIn = userInstanceIndex
			} else if (userInstance.user_status_in_instance === "blocked") {
				viewType = "blocked"
			} else {
				viewType = "enabled"
			}
			enrichedUserInstances.push({
				...userInstance,
				viewType
			})
		})

		return moveIndexToFirstPosition(enrichedUserInstances, indexOfInstanceWhoseUserIsCurrentlyLoggedIn)
	}

	const enrichedAndOrderedUserInstances = React.useMemo(
		() => getEnrichedAndOrderedUserInstances(allUserInstances, searchQuery),
		[allUserInstances, actualUserInstanceId, isAdminView, searchQuery]
	)

	useDidMount(() => {
		if (isSmallScreen) {
			handleBackButton(
				() => handleClose()
			)
		}
	})

	return (
		<ActionDialog
			fullWidth
			hideCloseButton
			loading={loading}
			openDialog={opened}
			onClose={handleClose}
			title={"Selecionar instância"}
			dialogTitleClassName={classes.title}
			dialogTitleIcon={<PeopleOutlineTwoToneIcon />}
			dialogTitleIconClassName={classes.titleIcon}
		>
			<Loading
				loading={loading}
			>
				<Grid
					container
				>
					<Grid item>
						<AlertContainer
							title="ATENÇÃO"
							alertType="warning"
							icon={<WarningIcon htmlColor={colors.yellow[500]} fontSize="large" />}
						>
							<Typography variant="body2">
								Ao mudar de instância, todo o trabalho não salvo em outras guias será perdido, incluindo fluxos de bots
							</Typography>
						</AlertContainer>

						{
							canViewUninassauFeaturePage && (
								<Typography
									variant="body2"
									className={classes.spacedText}
								>
									<Link
										href={`${window.location.origin}/admin/uninassau-features`}
										target="_BLANK"
									>
										Clique aqui
									</Link> para abrir a página de gestão unificada de instâncias
								</Typography>
							)
						}

						<Divider
							orientation="horizontal"
							size={2}
						></Divider>

						<MUIDivider />

						<Divider
							orientation="horizontal"
							size={2}
						></Divider>
					</Grid>
					<Grid container spacing={2}>
						<Grid item style={{ width: "100%" }}>
							<TextField
								value={searchQuery?.inputText}
								onChange={({ target }) => handleSearchChange(target.value)}
								placeholder="Pesquisar"
								variant="outlined"
								InputProps={{
									startAdornment: (
										<InputAdornment style={{ minHeight: 52 }} position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: searchQuery?.inputText && (
										<IconButton
											size="small"
											onClick={() => handleSearchChange("")}
										>
											<CloseIcon
												fontSize="small"
											/>
										</IconButton>
									)
								}}
								fullWidth
								size="small"
								helperText={validation.search}
								error={!!validation.search}
							/>
						</Grid>

						<Grid item style={{ minWidth: "100%" }}>
							{enrichedAndOrderedUserInstances.length > 0 ? enrichedAndOrderedUserInstances.map((enrichedAndOrderedUserInstance, index) => {
								return (
									<Grid
										item
										xs={12}
										key={index}
									>
										<InstanceListItem
											targetInstanceId={enrichedAndOrderedUserInstance.instance_id}
											targetInstanceName={String(enrichedAndOrderedUserInstance.nickname)}
											targetInstanceRoleName={enrichedAndOrderedUserInstance.user_role_name}
											listedView={enrichedAndOrderedUserInstance.viewType}
											onClick={async () => {
												if (enrichedAndOrderedUserInstance.viewType === "enabled") {
													setLoading(true)
													await Instance.redirectToTargetInstanceById(enrichedAndOrderedUserInstance.instance_id)
												}
											}}
										/>
										<Divider
											orientation="horizontal"
											size={2}
										></Divider>
									</Grid>
								)
							}) : (
								<Grid item xs={12}>
									<Typography align="center" style={{ fontSize: 24 }} variant="h2" className={classes.titleText}>
										Nenhuma instância encontrada
									</Typography>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Loading>
		</ActionDialog>
	)
}

InstanceSelectorDialogue.open = (props: InstanceSelectorDialogueProps) => {
	renderComponent(
		"instance-selector",
		<InstanceSelectorDialogue
			{...props}
		/>
	)
}

export default InstanceSelectorDialogue
